import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { ITourExtra } from 'app/shared/model/tour-extra.model';
import { getEntities as getTourExtras } from 'app/entities/tour-extra/tour-extra.reducer';
import { ITourExtraCategory } from 'app/shared/model/tour-extra-category.model';
import { getEntity, updateEntity, createEntity, reset } from './tour-extra-category.reducer';

export const TourExtraCategoryUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tourExtras = useAppSelector(state => state.tourExtra.entities);
  const tourExtraCategoryEntity = useAppSelector(state => state.tourExtraCategory.entity);
  const loading = useAppSelector(state => state.tourExtraCategory.loading);
  const updating = useAppSelector(state => state.tourExtraCategory.updating);
  const updateSuccess = useAppSelector(state => state.tourExtraCategory.updateSuccess);

  const [formData, setFormData] = useState<any>({
    createdBy: account?.id,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/tour-extra-category' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTourExtras({}));
  }, []);

  useEffect(() => {
    if (tourExtraCategoryEntity) {
      setFormData({
        ...tourExtraCategoryEntity,
        createdBy: tourExtraCategoryEntity?.createdBy?.id,
        extras: tourExtraCategoryEntity?.extras?.map(e => e.id.toString()),
      });
    }
  }, [tourExtraCategoryEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  const renderMultiSelectValue = (selected: any[]) => {
    return selected?.join(', ') || '';
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.enabled = values.enabled === true;

    const entity = {
      ...tourExtraCategoryEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      extras: values.extras ? mapIdList(values.extras) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.tourExtraCategory.home.createOrEditLabel">
            Create or edit a TourExtraCategory
          </Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField
                id="tour-extra-category-id"
                name="id"
                label={translate('global.field.id')}
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourExtraCategory.code')}
              id="tour-extra-category-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  id="tour-extra-category-enabled"
                  name="enabled"
                  data-cy="enabled"
                  checked={formData.enabled || false}
                  onChange={handleSwitchChange('enabled')}
                />
              }
              label={translate('jhipsterSampleApplicationApp.tourExtraCategory.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourExtraCategory.icon')}
              id="tour-extra-category-icon"
              name="icon"
              data-cy="icon"
              value={formData.icon || ''}
              onChange={handleChange('icon')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourExtraCategory.defaultImage')}
              id="tour-extra-category-defaultImage"
              name="defaultImage"
              data-cy="defaultImage"
              value={formData.defaultImage || ''}
              onChange={handleChange('defaultImage')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourExtraCategory.shopCategoryId')}
              id="tour-extra-category-shopCategoryId"
              name="shopCategoryId"
              data-cy="shopCategoryId"
              value={formData.shopCategoryId || ''}
              onChange={handleChange('shopCategoryId')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourExtraCategory.shopUrl')}
              id="tour-extra-category-shopUrl"
              name="shopUrl"
              data-cy="shopUrl"
              value={formData.shopUrl || ''}
              onChange={handleChange('shopUrl')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="tour-extra-category-extras-label">
                {translate('jhipsterSampleApplicationApp.tourExtraCategory.extra')}
              </InputLabel>
              <Select
                labelId="tour-extra-category-extras-label"
                id="tour-extra-category-extras"
                data-cy="extras"
                multiple
                value={formData.extras || []}
                label={translate('jhipsterSampleApplicationApp.tourExtraCategory.extra')}
                onChange={handleMultiSelectChange('extras')}
                renderValue={renderMultiSelectValue}
              >
                {tourExtras
                  ? tourExtras.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/tour-extra-category"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TourExtraCategoryUpdate;
